import { writable, get } from "svelte/store";
import { systemLog, directContext, callQueue, currentElementLockGroup } from "./storage.js";
import { goToMenu, parseDatumObject, clearHighlight } from "./direct.js";
import { deviceTypes } from "./component/artboard.js";
import { findLayer, unfindLayer } from "./component/layer.js";
import { findLegend, unfindLegend } from "./component/legend.js";
import { findAxis, unfindAxis } from "./component/axis.js";
import { findLabel, unfindLabel, findAnnotation, unfindAnnotation, queryMarkLabels } from "./component/annotation.js";
import { findTitle, unfindTitle } from "./component/title.js";

export const SpecTrees = writable({});
const from_tree = true;

export function setSpecTree(spec) {
  SpecTrees.update(trees => {
    let fronto = spec.fronto;
    let artboard_uid = spec.uid;
    // top-most: artboard
    let artboard = {
      name: spec.artboard.name,
      run: (_, node) => {
        directContext.set({ from_tree });
        goToMenu({ first: "artboard", subMenu: "editArtboard" });
        systemLog.update(log => { log.log({ message: "(DM) Edit artboard", from_tree, artboard_uid }); return log; });
      },
      icon: deviceTypes[spec.artboard.screen_type].icon,
      children: [],
      show_children: true,
      lock: []
    };
    // second: layout
    if (fronto.layout) {
      let layout = {
        name: "Layout",
        icon: "grid-fill",
        run: (_, node) => {
          directContext.set({ from_tree });
          goToMenu({ first: "layout" });
          systemLog.update(log => { log.log({ message: "(DM) Edit RC", from_tree, artboard_uid }); return log; });
        },
        children: [],
        lock: [],
        contextMenus: []
      };
      artboard.children.push(layout);
      if (fronto.layout.lock?.composition) {
        layout.lock.push("composition");
        layout.contextMenus.push({
          type: "lock", action: () => {
            let rule = {
              description: "(C) Unlock layout type",
              specifier: {
                role: "view",
              },
              action: "lock",
              option: {
                composition: false,
              },
            };
            callQueue.update((queue) => {
              queue.add([rule], get(currentElementLockGroup)).apply();
              return queue;
            });
          }, title: "Unlock layout type", icon: "unlock-fill"
        });
      } else {
        layout.contextMenus.push({
          type: "lock", action: () => {
            let rule = {
              description: "(C) Lock layout type",
              specifier: {
                role: "view",
              },
              action: "lock",
              option: {
                composition: true,
              },
            };
            callQueue.update((queue) => {
              queue.add([rule], get(currentElementLockGroup)).apply();
              return queue;
            });
          }, title: "Lock layout type", icon: "lock"
        });
      }
      if (fronto.layout.lock?.transpose) {
        layout.lock.push("axis-transpose");
        layout.contextMenus.push({
          type: "lock", action: () => {
            let rule = {
              description: "(C) Unlock transpose",
              specifier: {
                role: "view",
              },
              action: "lock",
              option: {
                transpose: false,
              },
            };
            callQueue.update((queue) => {
              queue.add([rule], get(currentElementLockGroup)).apply();
              return queue;
            });
          }, title: "Unlock transpose", icon: "unlock-fill"
        });
      } else {
        layout.contextMenus.push({
          type: "lock", action: () => {
            let rule = {
              description: "(C) Lock transpose",
              specifier: {
                role: "view",
              },
              action: "lock",
              option: {
                transpose: true,
              },
            };
            callQueue.update((queue) => {
              queue.add([rule], get(currentElementLockGroup)).apply();
              return queue;
            });
          }, title: "Lock transpose", icon: "lock"
        });
      }
      if (fronto.layout.split?.label) {
        let header = {
          name: "Multiples",
          icon: "tags",
          run: (_, node) => {
            directContext.set({ from_tree });
            goToMenu({ first: "layout" });
            systemLog.update(log => { log.log({ message: "(DM) Edit multiples header", from_tree, artboard_uid }); return log; });
          },
          lock: [],
          contextMenus: []
        }
        if (fronto.layout.lock?.header) {
          header.lock.push("label");
          header.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Unlock multiples header",
                specifier: {
                  role: "facet.label",
                },
                action: "lock",
                option: {
                  label: false,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Unlock multiples header", icon: "unlock-fill"
          });
        } else {
          header.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Lock multiples header",
                specifier: {
                  role: "facet.label",
                },
                action: "lock",
                option: {
                  label: true,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Lock multiples header", icon: "lock"
          });
        }
        layout.children.push(header);
      }
    }
    // second: projection
    if (fronto.layout?.projection) {
      let projection = {
        name: "Projection",
        icon: "map-fill",
        run: (_, node) => {
          directContext.set({ from_tree });
          goToMenu({ first: "projection" });
          systemLog.update(log => { log.log({ message: "(DM) Edit projection", from_tree, artboard_uid }); return log; });
        },
        lock: []
      };
      artboard.children.push(projection);
    }

    // second: axis
    let axis_node = {
      name: `Axes & headers`,
      icon: "info-circle",
      children: [],
      show_children: true,
      unfind: (_) => {
        clearHighlight();
      },
      lock: []
    }
    // second: legend
    let legend_node = {
      name: `Legends`,
      icon: "moisture",
      children: [],
      show_children: true,
      unfind: (_) => {
        clearHighlight();
      },
      lock: []
    }
    if (fronto.layer?.length > 0) {
      // second: mark & mark labels
      let layers = {
        name: "Layer" + (fronto.layer.length == 1 ? "" : "s") + ` (${fronto.layer.length})`,
        icon: "layers",
        children: [],
        show_children: true,
        run: (_, node) => {
          directContext.set({ from_tree });
          goToMenu({ first: "mark", finder: node.find });
          systemLog.update(log => { log.log({ message: "(DM) Edit layers", from_tree, artboard_uid }); return log; });
        },
        unfind: (_) => {
          clearHighlight();
        },
        lock: []
      };
      layers.find = (_) => {
        layers.children.forEach(child => child.find());
      }
      let layerNumberLimit;
      if (fronto?.layout?.composition === "single") {
        if (
          fronto?.layout?.row?.length >= 2 ||
          fronto?.layout?.column?.length >= 2
        )
          layerNumberLimit = 1;
        else layerNumberLimit = Infinity;
      } else if (fronto?.layout?.composition === "repeated") {
        layerNumberLimit = 1;
      } else {
        layerNumberLimit = Infinity;
      }
      fronto.layer.forEach((layer, li) => {
        let layerIndex = li;
        let layerName = layer.name;
        let layer_node = {
          name: layer.name || "Layer " + (li + 1),
          icon: "square",
          children: [],
          find: (_) => {
            findLayer(artboard_uid, layerName, layerIndex, layerNumberLimit, "find");
            let textIndex = 0;
            layer.text?.forEach((text, ti) => {
              if (text.type === "on-mark") {
                let field = text.field;
                findLabel(artboard_uid, { layerIndex, textIndex, field }, "find");
                textIndex += 1;
              }
            });
          },
          unfind: (_) => {
            unfindLayer();
            unfindLabel();
          },
          show_children: true,
          lock: []
        }
        let mark_node = {
          name: `Mark(s)/${layer.mark.type}`,
          icon: "circle-square",
          run: (_, node) => {
            directContext.set({ from_tree });
            goToMenu({ first: "mark", layerIndex, finder: node.find });
            systemLog.update(log => { log.log({ message: "(DM) Edit mark", from_tree, artboard_uid }); return log; });
          },
          find: (_) => {
            findLayer(artboard_uid, layerName, layerIndex, layerNumberLimit, "find");
          },
          unfind: (_) => {
            unfindLayer()
          },
          show_children: false,
          children: [],
          lock: [],
          contextMenus: []
        }

        if (layer.mark.lock) {
          layers.lock.push("mark at " + layerName);
          layer_node.lock.push("mark");
          mark_node.lock.push("this");
          mark_node.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Unlock mark type",
                specifier: {
                  role: "mark",
                },
                action: "lock",
                option: {
                  markType: false,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Unlock mark type", icon: "unlock-fill"
          });
        } else {
          mark_node.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Lock mark type",
                specifier: {
                  role: "mark",
                },
                action: "lock",
                option: {
                  markType: true,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Lock mark type", icon: "lock"
          });
        }
        let layer_marks0 = document.querySelectorAll(`#artboard-${artboard_uid} g.role-mark.F_layer_${layerIndex}__named_layer_0_marks path`);
        let layer_marks1 = document.querySelectorAll(`#artboard-${artboard_uid} g.role-mark.F_layer_${layerIndex}__named_layer_1_marks path`);
        let layer_marks2 = document.querySelectorAll(`#artboard-${artboard_uid} g.role-mark.F_layer_${layerIndex}__mark_layer_0_marks path`); // line/area
        let layer_marks3 = document.querySelectorAll(`#artboard-${artboard_uid} g.role-mark.F_layer_${layerIndex}__mark_layer_1_marks path`); // line/area
        [layer_marks0, layer_marks1, layer_marks2, layer_marks3].forEach(layer_marks => {
          Array.from(layer_marks)?.forEach((indMark, mi) => {
            let datum = parseDatumObject(indMark.getAttribute("aria-label"));
            let markRole = indMark.getAttribute("aria-roledescription");
            // line/area mark shouldn't involve position channel data;
            if (markRole === "line mark" || markRole === "area mark") {
              let xs = fronto.layout.column?.map((d) => { return d.field || d });
              let ys = fronto.layout.row?.map((d) => { return d.field || d });
              xs.forEach((f) => { delete datum[f]; });
              ys.forEach((f) => { delete datum[f]; });
            }
            if (markRole && datum) {
              let ind_mark_node = {
                name: `Mark ${markRole} (${mi})`,
                icon: "circle",
                run: (_, node) => {
                  directContext.set({ from_tree });
                  goToMenu({ first: "mark_individual", layerIndex, datum, mark: markRole });
                  systemLog.update(log => { log.log({ message: "(DM) Change idividual mark properties", from_tree, artboard_uid }); return log; })
                },
                find: (_) => {
                  indMark.classList.add("dupo-g-found-specific");
                },
                unfind: (_) => {
                  indMark.classList.remove("dupo-g-found-specific");
                },
              }
              mark_node.children.push(ind_mark_node);
            }
          });
        });
        mark_node.name = mark_node.name + ` (${mark_node.children.length})`;
        layer_node.children.push(mark_node);
        let tcount = 0, acount = 0, lcount = 0;
        layer.text?.forEach((text, ti) => {
          if (text.type === "on-mark") {
            let field = text.field;
            let textIndex = tcount;
            let text_node = {
              name: `Label(s)/${text.field || aggregate}`,
              icon: "tags",
              run: (_, node) => {
                directContext.set({ from_tree });
                goToMenu({ first: "annotation", subMenu: "mark-label", layerIndex, textIndex, field: (text.field || text.aggregate), finder: node.find });
                systemLog.update(log => { log.log({ message: "(DM) Edit mark labels from mark", from_tree, artboard_uid }); return log; });
              },
              find: (_) => {
                findLabel(artboard_uid, { layerIndex, textIndex, field }, "find");
              },
              unfind: (_) => {
                unfindLabel();
              },
              show_children: false,
              children: [],
              lock: [],
              contextMenus: []
            };
            if (text.lock) {
              if (!layers.lock.includes("mark labels at " + layerName)) layers.lock.push("mark labels at " + layerName);
              layer_node.lock.push("mark labels " + tcount);
              text_node.lock.push("this");
              text_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Unlock mark label for " + text.field,
                    specifier: {
                      role: "mark.label",
                      field: text.field,
                      layer: { id: layerName },
                    },
                    action: "lock",
                    option: {
                      label: false,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Unlock mark label", icon: "unlock-fill"
              });
            } else {
              text_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Lock mark label for " + text.field,
                    specifier: {
                      role: "mark.label",
                      field: text.field,
                      layer: { id: layerName },
                    },
                    action: "lock",
                    option: {
                      label: true,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Lock mark label", icon: "lock"
              });
            }
            let textItems = queryMarkLabels(artboard_uid, { layerIndex, textIndex, field });
            Array.from(textItems).forEach((textItem, ii) => {
              let text_item_node = {
                name: `Text item (${ii})`,
                icon: "type",
                find: (_) => {
                  textItem.classList.add("dupo-g-found-specific");
                },
                unfind: (_) => {
                  textItem.classList.remove("dupo-g-found-specific");
                },
              };
              text_node.children.push(text_item_node);
            })
            text_node.name = text_node.name + ` (${text_node.children.length})`;
            tcount += 1;
            layer_node.children.push(text_node);
          } else if (text.type === "on-axis") {
            let field = text.field;
            let textIndex = acount;
            let subrole;
            let designLock;
            if (fronto.layout?.row?.[0]?.field === field) {
              subrole = fronto.layout?.row.length == 2 ? "row" : "y";
              if (fronto.layout?.row?.[0]?.lock) designLock = true;
            }
            else if (fronto.layout?.row?.[1]?.field === field) {
              subrole = "y";
              if (fronto.layout?.row?.[1]?.lock) designLock = true;
            }
            else if (fronto.layout?.column?.[0]?.field === field) {
              subrole = fronto.layout?.column.length == 2 ? "column" : "x";
              if (fronto.layout?.column?.[0]?.lock) designLock = true;
            }
            else if (fronto.layout?.column?.[1]?.field === field) {
              subrole = "x";
              if (fronto.layout?.column?.[1]?.lock) designLock = true;
            }
            let rc_node = {
              name: `Axis ${textIndex} (${field})`,
              icon: "rulers",
              run: (_, node) => {
                directContext.set({ from_tree });
                goToMenu({ first: "axis", subMenu: "edit", layerIndex, textIndex, field, finder: node.find });
                systemLog.update(log => { log.log({ message: "(DM) Edit axis", from_tree, artboard_uid }); return log; });
              },
              find: (_) => {
                let textDesign = { layerIndex, textIndex: ti };
                findAxis(artboard_uid, { textDesign: [textDesign], subrole, field }, "find")
              },
              unfind: (_) => {
                unfindAxis("find");
              },
              lock: [],
              contextMenus: []
            }
            if (text.lock || designLock) {
              axis_node.lock.push(subrole + " axis");
            }
            if (text.lock) {
              rc_node.lock.push("label");
              rc_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Unlock axis label for " + text.field,
                    specifier: {
                      role: "axis.label",
                      field: text.field,
                    },
                    action: "lock",
                    option: {
                      label: false,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Unlock axis label", icon: "unlock-fill"
              });
            } else {
              rc_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Lock axis label for " + text.field,
                    specifier: {
                      role: "axis.label",
                      field: text.field,
                    },
                    action: "lock",
                    option: {
                      label: true,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Lock axis label", icon: "lock"
              });
            }
            if (designLock) {
              rc_node.lock.push("layout");
              rc_node.lock.push("grid");
              rc_node.lock.push("tick");
              rc_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Unlock axis for " + text.field,
                    specifier: {
                      role: "axis",
                      field: text.field,
                    },
                    action: "lock",
                    option: {
                      axis: false,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Unlock axis", icon: "unlock-fill"
              });
            } else {
              rc_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Lock axis for " + text.field,
                    specifier: {
                      role: "axis",
                      field: text.field,
                    },
                    action: "lock",
                    option: {
                      axis: true,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Lock axis", icon: "lock"
              });
            }
            axis_node.children.push(rc_node);
            acount += 1;
          } else if (text.type === "legend") {
            let field = text.field;
            let textIndex = lcount;
            let channel = text.channel;
            let legend_item_node = {
              name: `Legend ${textIndex} (${channel}, ${field})`,
              icon: "moisture",
              run: (_, node) => {
                directContext.set({ from_tree });
                goToMenu({ first: "legend", subMenu: "edit", channel, layerIndex, finder: node.find });
                systemLog.update(log => { log.log({ message: "(DM) Edit legend", from_tree, artboard_uid }); return log; });
              },
              find: (_) => {
                findLegend(artboard_uid, { layerIndex, channel }, "find");
              },
              unfind: (_) => {
                unfindLegend();
              },
              lock: [],
              contextMenus: []
            }
            if (text.lock || text.positionLock) {
              legend_node.lock.push("legend at " + layerName);
              legend_item_node.lock.push("this");
            }
            if (text.lock) {
              legend_item_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Unlock legend for " + field + ` (${channel})`,
                    specifier: {
                      role: "axis",
                      field,
                      channel,
                      layer: { id: layerName }
                    },
                    action: "lock",
                    option: {
                      legend: false,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Unlock legend", icon: "unlock-fill"
              });
            } else {
              legend_item_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Lock legend for " + field + ` (${channel})`,
                    specifier: {
                      role: "axis",
                      field,
                      channel,
                      layer: { id: layerName }
                    },
                    action: "lock",
                    option: {
                      legend: true,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Lock legend", icon: "Lock"
              });
            }
            if (text.positionLock) {
              legend_item_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Unlock legend position for " + field + ` (${channel})`,
                    specifier: {
                      role: "axis",
                      field,
                      channel,
                      layer: { id: layerName }
                    },
                    action: "lock",
                    option: {
                      position: false,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Unlock legend position", icon: "unlock-fill"
              });
            } else {
              legend_item_node.contextMenus.push({
                type: "lock", action: () => {
                  let rule = {
                    description: "(C) Lock legend position for " + field + ` (${channel})`,
                    specifier: {
                      role: "axis",
                      field,
                      channel,
                      layer: { id: layerName }
                    },
                    action: "lock",
                    option: {
                      position: true,
                    },
                  };
                  callQueue.update((queue) => {
                    queue.add([rule], get(currentElementLockGroup)).apply();
                    return queue;
                  });
                }, title: "Lock legend position", icon: "Lock"
              });
            }
            legend_node.children.push(legend_item_node);
            lcount += 1;
          }
        });
        layers.children.push(layer_node);
      });
      artboard.children.push(layers);
    }
    if (axis_node.children.length > 0) {
      axis_node.name = axis_node.name + ` (${axis_node.children.length})`;
      axis_node.find = (_) => {
        axis_node.children.forEach(child => child.find());
      }
      artboard.children.push(axis_node);
    }
    if (legend_node.children.length > 0) {
      legend_node.name = legend_node.name + (legend_node.children.length > 1 ? "s" : "") + ` (${legend_node.children.length})`;
      legend_node.find = (_) => {
        legend_node.children.forEach(child => child.find());
      }
      artboard.children.push(legend_node);
    }
    // second: titles
    let title_node = {
      name: "Title",
      icon: "card-heading",
      children: [],
      show_children: true,
      unfind: (_) => { clearHighlight() },
      lock: []
    }
    fronto.title?.items?.forEach((title_item, ti) => {
      let titleIndex = ti, titleName = title_item.name;
      let title_item_node = {
        name: `Title ${ti}`,
        icon: "text",
        run: (_, node) => {
          directContext.set({ from_tree });
          goToMenu({ first: "title", subMenu: "edit", titleIndex, titleName, finder: node.find });
          systemLog.update(log => { log.log({ message: "(DM) Edit title", from_tree, artboard_uid }); return log; });
        },
        find: (_) => {
          findTitle(artboard_uid, { name: title_item.name }, "find");
        },
        unfind: (_) => {
          unfindTitle("find");
        },
        lock: [],
        contextMenus: []
      };
      if (title_item.lock) {
        title_node.lock.push(titleName);
        title_item_node.lock.push("this");
        title_item_node.contextMenus.push({
          type: "lock", action: () => {
            let rule = {
              description: "(C) Unlock title " + titleName,
              specifier: {
                role: "title",
                id: titleName
              },
              action: "lock",
              option: {
                title: false,
              },
            };
            callQueue.update((queue) => {
              queue.add([rule], get(currentElementLockGroup)).apply();
              return queue;
            });
          }, title: "Unlock title", icon: "unlock-fill"
        });
      } else {
        title_item_node.contextMenus.push({
          type: "lock", action: () => {
            let rule = {
              description: "(C) Lock title " + titleName,
              specifier: {
                role: "title",
                id: titleName
              },
              action: "lock",
              option: {
                title: true,
              },
            };
            callQueue.update((queue) => {
              queue.add([rule], get(currentElementLockGroup)).apply();
              return queue;
            });
          }, title: "Lock title", icon: "lock"
        });
      }
      title_node.children.push(title_item_node);
    })
    if (fronto.layout?.split) {
      let split_field = fronto.layout?.split.field;
      if (split_field) {
        let split_node = {
          name: `Multiples header (${split_field})`,
          icon: "text",
          run: (_, node) => {
            directContext.set({ from_tree });
            goToMenu({ first: "title", subMenu: "edit-multiples-header", finder: node.find });
            systemLog.update(log => { log.log({ message: "(DM) Edit multipels header", from_tree, artboard_uid }); return log; });
          },
          find: (_) => {
            findTitle(artboard_uid, "multiples", "find");
          },
          unfind: (_) => {
            unfindTitle("find");
          }
        }
        title_node.children.push(split_node);
      }
    }
    if (title_node.children.length > 0) {
      title_node.name = title_node.name + (title_node.children.length > 1 ? "s" : "") + ` (${title_node.children.length})`;
      title_node.find = (_) => {
        title_node.children.forEach(child => child.find());
      }
      artboard.children.push(title_node);
    }
    // second: annotations
    if (fronto.annotation?.items?.length > 0) {
      let annotation_node = {
        name: "Annotation" + (fronto.annotation?.items?.length > 1 ? "s" : "") + ` (${fronto.annotation?.items?.length})`,
        icon: "tags",
        children: [],
        show_children: true,
        unfind: (_) => { clearHighlight(); },
        lock: []
      };
      fronto.annotation?.items.forEach((anno_item, ai) => {
        let annoIndex = ai, annoName = anno_item.name
        let annotation_item_node = {
          name: `Annotation (${annoIndex})`,
          icon: "text",
          run: (_, node) => {
            directContext.set({ from_tree });
            goToMenu({ first: "annotation", subMenu: "annotation", annoIndex, annoName, finder: node.find });
            systemLog.update(log => { log.log({ message: "(DM) Edit annotation", from_tree, artboard_uid }); return log; });
          },
          find: (_) => {
            findAnnotation(artboard_uid, { name: annoName }, "find");
          },
          unfind: (_) => {
            unfindAnnotation();
          },
          lock: [],
          contextMenus: []
        };
        if (anno_item.lock || anno_item.positionLock) {
          annotation_node.lock.push(annoName);
          annotation_item_node.lock.push("this");
        }
        if (anno_item.lock) {
          annotation_item_node.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Unlock annotation " + annoName,
                specifier: {
                  role: "annotation",
                  id: annoName
                },
                action: "lock",
                option: {
                  annotation: false,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Unlock annotation", icon: "unlock-fill"
          });
        } else {
          annotation_item_node.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Lock annotation " + annoName,
                specifier: {
                  role: "annotation",
                  id: annoName
                },
                action: "lock",
                option: {
                  annotation: true,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Lock annotation", icon: "lock"
          });
        }
        if (anno_item.positionLock) {
          annotation_item_node.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Unlock annotation position " + annoName,
                specifier: {
                  role: "annotation",
                  id: annoName
                },
                action: "lock",
                option: {
                  position: false,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Unlock annotation position", icon: "unlock-fill"
          });
        } else {
          annotation_item_node.contextMenus.push({
            type: "lock", action: () => {
              let rule = {
                description: "(C) Lock annotation position " + annoName,
                specifier: {
                  role: "annotation",
                  id: annoName
                },
                action: "lock",
                option: {
                  position: true,
                },
              };
              callQueue.update((queue) => {
                queue.add([rule], get(currentElementLockGroup)).apply();
                return queue;
              });
            }, title: "Lock annotation position", icon: "lock"
          });
        }
        annotation_node.children.push(annotation_item_node);
      });
      annotation_node.find = (_) => {
        annotation_node.children.forEach(child => child.find());
      }
      artboard.children.push(annotation_node);
    }
    // second: interactions (todo)
    trees[artboard_uid] = artboard;
    window.specTrees = trees;
    return trees;
  })
}